import { Link, useLocation } from "wouter";
import {
  Inbox,
  Library,
  Search,
  FolderKanban,
  CheckSquare,
  MessagesSquare,
  Settings,
  Tag,
  Plug,
  SquarePlus,
  Plus,
  LogOut,
  ChevronDown,
  PanelLeftClose,
  PanelLeftOpen,
  Bookmark,
  Clock,
  Lightbulb,
  Eye,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useUiStore } from "@/lib/stores/uiStore";
import { useAuthStore } from "@/lib/stores/authStore";
import { useItemsStore } from "@/lib/stores/itemsStore";
import { useChatStore } from "@/lib/stores/chatStore";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { savedFilters, recentSearches, assistantSuggestions, taskViews, planUsage } from "@/lib/data/extras";

const NAV_MAIN = [
  { to: "/inbox", label: "Inbox", icon: Inbox, badge: "inbox" },
  { to: "/biblioteca", label: "Biblioteca", icon: Library },
  { to: "/buscar", label: "Búsqueda", icon: Search },
  { to: "/proyectos", label: "Proyectos", icon: FolderKanban },
  { to: "/tareas", label: "Tareas", icon: CheckSquare },
  { to: "/etiquetas", label: "Etiquetas", icon: Tag },
];

const NAV_SECONDARY = [
  { to: "/integraciones", label: "Integraciones", icon: Plug },
  { to: "/asistente", label: "Asistente", icon: MessagesSquare },
  { to: "/ajustes", label: "Ajustes", icon: Settings },
];

const NavLink = ({ to, label, icon: Icon, badge, collapsed, active }) => (
  <Link
    href={to}
    data-testid={`sidebar-nav-${to.slice(1)}`}
    aria-label={label}
    className={cn(
      "relative flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm font-medium transition-colors",
      collapsed && "justify-center px-0",
      active ? "bg-accent text-accent-foreground" : "text-muted-foreground hover:text-foreground hover:bg-secondary/80",
    )}
  >
    {active && <span className="absolute left-0 top-1.5 bottom-1.5 w-0.5 rounded-full bg-primary" />}
    <Icon className="h-[18px] w-[18px] shrink-0" strokeWidth={1.75} />
    {!collapsed && <span className="flex-1 truncate">{label}</span>}
    {!collapsed && badge != null && badge > 0 && (
      <span className="rounded-full bg-accent text-accent-foreground px-1.5 py-0.5 text-[11px] font-semibold tabular-nums">
        {badge}
      </span>
    )}
  </Link>
);

// Tarjeta contextual del pie del sidebar — cambia según la página (maquetas 2026-07-02).
const ContextCard = ({ location }) => {
  const sendChat = useChatStore((s) => s.send);

  if (location.startsWith("/biblioteca")) {
    return (
      <div className="rounded-lg border bg-card p-3" data-testid="sidebar-context-card">
        <p className="text-xs font-semibold text-muted-foreground mb-2">Filtros guardados</p>
        <div className="space-y-1">
          {savedFilters.map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => toast(`Filtro «${f.name}» aplicado (demo).`)}
              className="w-full flex items-center gap-2 rounded-md px-1.5 py-1 text-[13px] text-muted-foreground hover:text-foreground hover:bg-secondary/70 transition-colors"
            >
              <Bookmark className="h-3.5 w-3.5 shrink-0" strokeWidth={1.75} />
              <span className="flex-1 truncate text-left">{f.name}</span>
              <span className="text-xs tabular-nums">{f.count}</span>
            </button>
          ))}
          <button type="button" onClick={() => toast("Podrás guardar filtros en la versión final.")} className="flex items-center gap-1.5 px-1.5 py-1 text-[13px] text-primary hover:underline">
            <Plus className="h-3.5 w-3.5" /> Nuevo filtro
          </button>
        </div>
      </div>
    );
  }

  if (location.startsWith("/buscar")) {
    return (
      <div className="rounded-lg border bg-card p-3" data-testid="sidebar-context-card">
        <p className="text-xs font-semibold text-muted-foreground mb-2">Búsquedas recientes</p>
        <div className="space-y-1">
          {recentSearches.slice(0, 5).map((q) => (
            <p key={q} className="flex items-center gap-2 px-1.5 py-1 text-[13px] text-muted-foreground truncate">
              <Clock className="h-3.5 w-3.5 shrink-0" strokeWidth={1.75} /> {q}
            </p>
          ))}
        </div>
      </div>
    );
  }

  if (location.startsWith("/asistente")) {
    return (
      <div className="rounded-lg border bg-card p-3" data-testid="sidebar-context-card">
        <p className="text-xs font-semibold text-muted-foreground mb-2">Sugerencias para empezar</p>
        <div className="space-y-1">
          {assistantSuggestions.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => sendChat(s)}
              className="w-full flex items-start gap-2 rounded-md px-1.5 py-1 text-[13px] text-muted-foreground hover:text-foreground hover:bg-secondary/70 text-left transition-colors"
            >
              <Lightbulb className="h-3.5 w-3.5 shrink-0 mt-0.5" strokeWidth={1.75} />
              <span className="line-clamp-2">{s}</span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  if (location.startsWith("/tareas") || location.startsWith("/proyectos")) {
    return (
      <div className="rounded-lg border bg-card p-3" data-testid="sidebar-context-card">
        <p className="text-xs font-semibold text-muted-foreground mb-2">Vistas guardadas</p>
        <div className="space-y-1">
          {taskViews.map((v) => (
            <button
              key={v.id}
              type="button"
              onClick={() => toast(`Vista «${v.name}» (demo).`)}
              className="w-full flex items-center gap-2 rounded-md px-1.5 py-1 text-[13px] text-muted-foreground hover:text-foreground hover:bg-secondary/70 transition-colors"
            >
              <Eye className="h-3.5 w-3.5 shrink-0" strokeWidth={1.75} />
              <span className="flex-1 truncate text-left">{v.name}</span>
              <span className="text-xs tabular-nums">{v.count}</span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  if (location.startsWith("/ajustes") || location.startsWith("/integraciones")) {
    return (
      <div className="rounded-lg border bg-card p-3" data-testid="sidebar-context-card">
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-semibold text-muted-foreground">Uso del plan</p>
          <span className="rounded-full bg-teal-50 text-teal-700 dark:bg-teal-950/60 dark:text-teal-400 px-2 py-0.5 text-[11px] font-medium">Pro</span>
        </div>
        <div className="h-1.5 rounded-full bg-secondary overflow-hidden">
          <div className="h-full rounded-full bg-primary" style={{ width: `${planUsage.storagePct}%` }} />
        </div>
        <p className="mt-2 text-xs text-muted-foreground">
          {planUsage.storageUsed} de {planUsage.storageTotal} usados
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          {planUsage.aiQuota.toLocaleString("es-ES")} resúmenes IA / mes · {planUsage.aiUsed.toLocaleString("es-ES")} usados
        </p>
      </div>
    );
  }

  // Inbox y resto: promo de captura
  return (
    <div className="rounded-lg border bg-card p-3" data-testid="sidebar-context-card">
      <p className="text-sm font-semibold">Comparte desde cualquier lugar</p>
      <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
        Extensión, atajos y apps móviles para capturar al instante.
      </p>
      <button type="button" onClick={() => toast("Guía de captura disponible en la versión final.")} className="mt-2 text-[13px] text-primary font-medium hover:underline">
        Ver cómo funciona →
      </button>
    </div>
  );
};

export const Sidebar = () => {
  const [location] = useLocation();
  const collapsed = useUiStore((s) => s.sidebarCollapsed);
  const toggleSidebar = useUiStore((s) => s.toggleSidebar);
  const openCapture = useUiStore((s) => s.openCapture);
  const { user, logout } = useAuthStore();
  const items = useItemsStore((s) => s.items);
  const inboxCount = items.filter((i) => i.status === "inbox").length;

  return (
    <aside
      className={cn(
        "hidden md:flex flex-col border-r bg-stone-100/60 dark:bg-stone-900/40 transition-[width] duration-200 shrink-0",
        collapsed ? "w-16" : "w-64",
      )}
      data-testid="sidebar"
    >
      <div className={cn("flex items-center gap-2 h-14 px-3", collapsed && "justify-center px-0")}>
        <div
          role={collapsed ? "button" : undefined}
          onClick={collapsed ? toggleSidebar : undefined}
          title={collapsed ? "Expandir menú" : undefined}
          className={cn(
            "w-8 h-8 rounded-lg bg-primary text-primary-foreground grid place-items-center font-serif text-lg shrink-0",
            collapsed && "cursor-pointer",
          )}
        >
          A
        </div>
        {!collapsed && <span className="font-serif text-lg tracking-tight">Aviando</span>}
        {!collapsed && (
          <button
            type="button"
            onClick={toggleSidebar}
            aria-label="Contraer barra lateral"
            title="Contraer menú"
            data-testid="sidebar-collapse-button"
            className="ml-auto text-muted-foreground/60 hover:text-foreground transition-colors"
          >
            <PanelLeftClose className="h-4 w-4" strokeWidth={1.75} />
          </button>
        )}
      </div>

      {/* Expandir SIEMPRE arriba, en el mismo sitio donde se contrajo (feedback hito 6) */}
      {collapsed && (
        <div className="px-2 pb-2">
          <button
            type="button"
            onClick={toggleSidebar}
            aria-label="Expandir barra lateral"
            title="Expandir menú"
            data-testid="sidebar-expand-button"
            className="w-full h-9 rounded-md border bg-card grid place-items-center text-muted-foreground hover:text-foreground hover:border-teal-300/60 transition-colors"
          >
            <PanelLeftOpen className="h-4 w-4" strokeWidth={1.75} />
          </button>
        </div>
      )}

      <nav className="flex-1 px-2 pt-1 overflow-y-auto scrollbar-thin" aria-label="Navegación principal">
        <div className="space-y-0.5">
          {NAV_MAIN.map((n) => (
            <NavLink
              key={n.to}
              {...n}
              badge={n.badge === "inbox" ? inboxCount : undefined}
              collapsed={collapsed}
              active={location.startsWith(n.to)}
            />
          ))}
        </div>

        <div className="my-3 h-px bg-border" />

        <div className="space-y-0.5">
          <button
            type="button"
            onClick={openCapture}
            data-testid="share-content-button"
            aria-label="Capturar contenido"
            className={cn(
              "w-full flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-secondary/80 transition-colors",
              collapsed && "justify-center px-0",
            )}
          >
            <SquarePlus className="h-[18px] w-[18px] shrink-0" strokeWidth={1.75} />
            {!collapsed && <span className="flex-1 text-left">Capturar</span>}
            {!collapsed && <Plus className="h-4 w-4 text-muted-foreground/60" strokeWidth={1.75} />}
          </button>
          {NAV_SECONDARY.map((n) => (
            <NavLink key={n.to} {...n} collapsed={collapsed} active={location.startsWith(n.to)} />
          ))}
        </div>
      </nav>

      {!collapsed && (
        <div className="px-3 pb-2">
          <ContextCard location={location} />
        </div>
      )}

      <div className={cn("border-t p-3", collapsed && "p-2")}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              data-testid="user-menu-button"
              className={cn(
                "w-full flex items-center gap-2.5 rounded-md p-1.5 hover:bg-secondary/80 transition-colors",
                collapsed && "justify-center p-1",
              )}
            >
              <div className="w-8 h-8 rounded-full bg-accent text-accent-foreground grid place-items-center text-xs font-semibold shrink-0">
                {user?.initials}
              </div>
              {!collapsed && (
                <>
                  <div className="min-w-0 flex-1 text-left">
                    <p className="text-sm font-medium truncate">{user?.name}</p>
                    <p className="text-xs text-muted-foreground">Cuenta Pro</p>
                  </div>
                  <ChevronDown className="h-4 w-4 text-muted-foreground/60" strokeWidth={1.75} />
                </>
              )}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" side="top" className="w-52">
            <DropdownMenuItem asChild>
              <Link href="/ajustes" className="flex items-center gap-2">
                <Settings className="h-4 w-4" strokeWidth={1.75} /> Ajustes
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={logout} data-testid="logout-button" className="flex items-center gap-2">
              <LogOut className="h-4 w-4" strokeWidth={1.75} /> Cerrar sesión
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </aside>
  );
};
